/**
 * Progressive Image Component
 * LQIP (Low Quality Image Placeholder) → Full quality with smooth transition
 * Prevents layout shift with fixed aspect ratio
 */

import { useState, useEffect, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface ProgressiveImageProps {
  src: string;
  thumbnailSrc?: string;
  alt: string;
  aspectRatio?: string; // e.g., "4/3", "16/9"
  className?: string;
  onLoad?: () => void;
  priority?: boolean; // Skip lazy loading for above-fold images
  layoutId?: string; // For shared element transitions
}

/**
 * Progressive image with blur-up loading effect
 * Shows blurred thumbnail immediately, swaps to full when decoded
 */
export const ProgressiveImage = memo(function ProgressiveImage({
  src,
  thumbnailSrc,
  alt,
  aspectRatio = '4/3',
  className,
  onLoad,
  priority = false,
  layoutId,
}: ProgressiveImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isThumbLoaded, setIsThumbLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Reset state when src changes
  useEffect(() => {
    setIsLoaded(false);
    setIsThumbLoaded(false);
    setHasError(false);
  }, [src]);

  const handleThumbLoad = useCallback(() => {
    setIsThumbLoaded(true);
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  // Use the main src as thumbnail if none provided
  const thumb = thumbnailSrc || src;

  return (
    <div
      className={clsx(
        'relative overflow-hidden bg-workshop-bg',
        className
      )}
      style={{ aspectRatio }}
    >
      {/* Skeleton shimmer - shows until thumbnail loads */}
      <AnimatePresence>
        {!isThumbLoaded && !hasError && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="absolute inset-0 skeleton-shimmer bg-workshop-surface"
          />
        )}
      </AnimatePresence>

      {/* Blurred thumbnail (LQIP) */}
      {thumb && !hasError && (
        <img
          src={thumb}
          alt=""
          aria-hidden="true"
          onLoad={handleThumbLoad}
          onError={handleError}
          className={clsx(
            'absolute inset-0 w-full h-full object-cover',
            'transition-all duration-150',
            isLoaded ? 'opacity-0' : 'opacity-100',
            !isThumbLoaded && 'opacity-0'
          )}
          style={{
            filter: isLoaded ? 'blur(0)' : 'blur(8px)',
            transform: isLoaded ? 'scale(1)' : 'scale(1.02)',
          }}
          loading="eager"
          decoding="async"
        />
      )}

      {/* Full quality image */}
      {!hasError && (
        <motion.img
          layoutId={layoutId}
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={clsx(
            'absolute inset-0 w-full h-full object-cover',
            'transition-opacity duration-120',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          // Tiny settle animation on load
          initial={{ scale: 1.01 }}
          animate={{ scale: isLoaded ? 1 : 1.01 }}
          transition={{ duration: 0.12, ease: [0.25, 0.1, 0.25, 1] }}
        />
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-workshop-surface">
          <div className="text-center text-workshop-text-muted">
            <svg
              className="w-8 h-8 mx-auto mb-2 opacity-50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-xs">Image unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * Image with shared element transition support
 * Used for card → detail transitions
 */
interface SharedImageProps extends ProgressiveImageProps {
  imageId: string; // Unique ID for shared element matching
}

export const SharedImage = memo(function SharedImage({
  imageId,
  ...props
}: SharedImageProps) {
  return (
    <ProgressiveImage
      {...props}
      layoutId={`image-${imageId}`}
    />
  );
});

export default ProgressiveImage;
